import { useEffect, useState } from "react";
import { SettingsAPI, AppointmentConfig } from "../services/settings.service";

// Clinic appointment config (the DEFAULT slot duration + daily booking cap) is
// configured in clinic settings and consumed elsewhere: the Roster schedule
// editor seeds its per-day defaults from it, and the Book Appointment flow uses
// the cap as a fallback when a doctor's schedule sets no per-day limit.
//
// The settings screen owns the writes (cacheAppointmentConfig); other screens
// read via useAppointmentConfig(). Mirrors useHrfSettings: cache-seed for warm/
// offline reads, API-refresh on mount, plus an in-page event so a save
// propagates immediately — even to the always-mounted Book Appointment modal.

const CACHE_KEY = "kgz_appointment_config_cache";
const UPDATE_EVENT = "kgz:appointment-config-updated";

// Fallback until the real config is known. Matches the backend column defaults.
const DEFAULT_CONFIG: AppointmentConfig = {
  slot_duration_minutes: 15,
  max_daily_bookings:    30,
  allow_online_booking:  false,
};

function readCache(): AppointmentConfig | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as AppointmentConfig) : null;
  } catch {
    return null;
  }
}

function writeCache(data: AppointmentConfig): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

/**
 * Persist the latest appointment config and notify live consumers so a Settings
 * save propagates immediately — to Roster, and to an already-open Book
 * Appointment modal — without a reload.
 */
export function cacheAppointmentConfig(data: AppointmentConfig): void {
  writeCache(data);
  try {
    window.dispatchEvent(new CustomEvent<AppointmentConfig>(UPDATE_EVENT, { detail: data }));
  } catch {
    /* no window (SSR/test) — non-fatal */
  }
}

/**
 * Read the clinic's appointment config. Seeds synchronously from the cache,
 * refreshes from the API on mount, and live-updates when settings are saved
 * this session (in-page event) or in another tab (storage event). Falls back to
 * the cache / defaults when the network is unavailable.
 */
export function useAppointmentConfig(): AppointmentConfig {
  const [config, setConfig] = useState<AppointmentConfig>(() => readCache() ?? DEFAULT_CONFIG);

  useEffect(() => {
    let cancelled = false;

    SettingsAPI.getAppointmentConfig()
      .then(data => {
        if (cancelled) return;
        setConfig(data);
        writeCache(data);
      })
      .catch(() => { /* keep cached / default — fail open */ });

    const onUpdate = (e: Event) => {
      const detail = (e as CustomEvent<AppointmentConfig>).detail;
      setConfig(detail ?? readCache() ?? DEFAULT_CONFIG);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === CACHE_KEY) setConfig(readCache() ?? DEFAULT_CONFIG);
    };
    window.addEventListener(UPDATE_EVENT, onUpdate);
    window.addEventListener("storage", onStorage);

    return () => {
      cancelled = true;
      window.removeEventListener(UPDATE_EVENT, onUpdate);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return config;
}
